import { Component, OnInit, Input } from '@angular/core';
import { ReportDashboardConfig, ReportDashboardComponentConfig } from '@liquitics/transaction';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { getIconForComponent } from '../report-dashboard-constants';

class ComponentConfigBox {
    componentConfigs: ReportDashboardComponentConfig[];
    rowIndex: number;
    colIndex: number;
    colSpan: number;

    constructor(ccs: ReportDashboardComponentConfig[], componentIndex: number) {
        this.componentConfigs = [...ccs];
        const layout = ccs[0].layout || {
            rowIndex: 0,
            colSpan: 4,
            colIndex: componentIndex % 4
        };
        this.rowIndex = layout.rowIndex;
        this.colIndex = layout.colIndex;
        this.colSpan = layout.colSpan;
    }

    updateComponentConfig() {
        this.componentConfigs
            .forEach((componentConfig) => {
                const layout = componentConfig.layout || {
                    rowIndex: 0,
                    colIndex: 0,
                    colSpan: 4,
                };
                componentConfig.layout = layout;
                layout.rowIndex = this.rowIndex;
                layout.colIndex = this.colIndex;
                layout.colSpan = this.colSpan;
            });
    }
}


@Component({
    selector: 'app-dashboard-layout-builder',
    templateUrl: './dashboard-layout-builder.component.html',
    styleUrls: ['./dashboard-layout-builder.component.scss']
})
export class DashboardLayoutBuilderComponent implements OnInit {
    @Input()
    reportDashboardConfig: ReportDashboardConfig;
    componentBoxRows: ComponentConfigBox[][];
    selectedRow = -1;
    selectedCol = -1;
    selectedStackIndex = -1;

    constructor() {
    }

    ngOnInit(): void {
        this.populateComponentRows();
    }
    populateComponentRows() {
        // this.componentsRows = [];
        // let usedColSpan = 0;
        // let currentComponentsRow: ComponentConfigBox[] = [];
        if (this.isFreshLayout()) {
            this.resetLayoutsToDefault();
        } else {
            const componentsByRowIndex = this.reportDashboardConfig.componentConfigs
                .reduce((grouped, cc) => {
                    let rowIndex = cc.layout?.rowIndex;
                    if (rowIndex === undefined) {
                        rowIndex = 0;
                    }
                    grouped[rowIndex] = grouped[rowIndex] || [];
                    grouped[rowIndex].push(cc);
                    return grouped;
                }, [] as ReportDashboardComponentConfig[][]);
            this.componentBoxRows = componentsByRowIndex
                .map((rowComponents, rowIndex) => {
                    const componentsByColIndex = rowComponents
                        .reduce((grouped, cc, index) => {
                            let colIndex = cc.layout?.colIndex;
                            if (colIndex === undefined) {
                                colIndex = index;
                            }
                            grouped[colIndex] = grouped[colIndex] || [];
                            grouped[colIndex].push(cc);
                            return grouped;
                        }, [])
                        .filter((grouped) => {
                            return grouped !== undefined && grouped.length > 0;
                        });
                    const componentsRow = componentsByColIndex
                        .map((components, colIndex) => {
                            const ccb = new ComponentConfigBox(components, colIndex);
                            return ccb;
                        });
                    return componentsRow;
                });
        }
        this.reindexAllComponentBoxes();
    }

    reindexAllComponentBoxes() {
        this.componentBoxRows
            .forEach((componentBoxRow, rowIndex) => {
                componentBoxRow
                    .forEach((ccb, columnIndex) => {
                        ccb.rowIndex = rowIndex;
                        ccb.colIndex = columnIndex;
                    });
            });
    }

    isFreshLayout(): boolean {
        return this.reportDashboardConfig.componentConfigs
            .reduce((isFresh, cc) => {
                if (cc.layout) {
                    if (cc.layout.colIndex !== undefined &&
                        cc.layout.rowIndex !== undefined &&
                        cc.layout.colSpan !== undefined) {
                        return false;
                    }
                }
                return isFresh;
            }, true);
    }

    resetLayoutsToDefault() {
        const defaultComponentRows = [];
        this.reportDashboardConfig.componentConfigs
            .forEach((cc, ccIndex) => {
                const ccb: ComponentConfigBox = new ComponentConfigBox([cc], ccIndex);
                let componentRow: ComponentConfigBox[];
                if (defaultComponentRows.length === 0) {
                    componentRow = [];
                    defaultComponentRows.push(componentRow);
                } else {
                    componentRow = defaultComponentRows[defaultComponentRows.length - 1];
                    if (componentRow.length >= 3) {
                        componentRow = [];
                        defaultComponentRows.push(componentRow);
                    }
                }
                componentRow.push(ccb);
                ccb.rowIndex = defaultComponentRows.length - 1;
                ccb.colSpan = 4;
                ccb.colIndex = componentRow.length - 1;
            });
        this.componentBoxRows = defaultComponentRows;
    }

    getIcon(componentConfig: ReportDashboardComponentConfig): string {
        return getIconForComponent(componentConfig.queryRequest?.usage || '', componentConfig.type, componentConfig.chart?.chartType);
    }

    drop(event: CdkDragDrop<string[]>, index: number) {
        moveItemInArray(this.componentBoxRows[index], event.previousIndex, event.currentIndex);
    }


    moveSelected(direction) {
        this.move(this.selectedRow, this.selectedCol, direction, this.selectedStackIndex);
    }

    exceedsAllowedSpan(rowToInspect: ComponentConfigBox[]): boolean {
        return (this.calcUsedSpan(rowToInspect) > 12);
    }

    calcUsedSpan(rowToInspect: ComponentConfigBox[]): number {
        return (rowToInspect || []).reduce((span, cc) => {
            span += cc.colSpan;
            return span;
        }, 0);
    }

    move(r: number, c: number, direction: 'SHRINK' | 'GROW' | 'UP' | 'DOWN' | 'LEFT' | 'RIGHT', stackIndex?: number) {
        // const componentBoxRow: ComponentConfigBox[] = this.componentBoxRows[r];
        // const component = componentBoxRow[c];
        const row = this.componentBoxRows[r];
        const ccBox = row[c];
        const selectedIdentifier = ccBox.componentConfigs[stackIndex].identifier;

        if (row && ccBox) {
            const usedSpan = this.calcUsedSpan(row);
            if (direction === 'GROW') {
                if (usedSpan >= 12) {
                    // Cant grow beyond max row size
                    return;
                }
                ccBox.colSpan++;
            } else if (direction === 'SHRINK') {
                if (ccBox.colSpan <= 2) {
                    // Cant shrink beyond min size
                    return;
                }
                ccBox.colSpan--;
            } else if (direction === 'UP') {
                if (stackIndex === 0) {
                    // First one in stack, so move to prev row
                    let prevRow;
                    if (r === 0) {
                        if (row.length === 1 && ccBox.componentConfigs.length === 1) {
                            // Solo component in a solo row, so no need to create a new row or move
                            return;
                        }
                        // Need to add a row
                        prevRow = [];
                        this.componentBoxRows.unshift(prevRow);
                    } else {
                        prevRow = this.componentBoxRows[r - 1];
                    }
                    const poppedCC = ccBox.componentConfigs.splice(stackIndex, 1)[0];
                    if (ccBox.componentConfigs.length === 0) {
                        // Nothing left so remove the box
                        row.splice(c, 1);
                        if (row.length === 0) {
                            // If nothing remains, lets get rid of the row
                            this.componentBoxRows.splice(r, 1);
                        }
                    }
                    if (prevRow.length === 0) {
                        // Target row is empty, we need a new box
                        const newCCB = new ComponentConfigBox([poppedCC], 0);
                        const usedPrevSpan = this.calcUsedSpan(prevRow);
                        const newSpan = Math.max((12 - usedPrevSpan), 2);
                        newCCB.rowIndex = r + 1;
                        newCCB.colIndex = 0;
                        newCCB.colSpan = newSpan;
                        prevRow.push(newCCB);
                    } else {
                        // Tack it onto the end
                        prevRow[0].componentConfigs.push(poppedCC);
                    }
                } else {
                    moveItemInArray(ccBox.componentConfigs, stackIndex, stackIndex - 1);
                }
            } else if (direction === 'DOWN') {
                if (stackIndex === ccBox.componentConfigs.length - 1) {
                    // Last one in stack, so move to next row
                    let nextRow;
                    if (r === (this.componentBoxRows.length - 1)) {
                        if (row.length === 1 && ccBox.componentConfigs.length === 1) {
                            // Solo component in a solo row, so no need to create a new row or move
                            return;
                        }
                        // Need to add a row
                        nextRow = [];
                        this.componentBoxRows.push(nextRow);
                    } else {
                        nextRow = this.componentBoxRows[r + 1];
                    }
                    const poppedCC = ccBox.componentConfigs.splice(stackIndex, 1)[0];
                    if (ccBox.componentConfigs.length === 0) {
                        // Nothing left so remove the box
                        row.splice(c, 1);
                        if (row.length === 0) {
                            // If nothing remains, lets get rid of the row
                            this.componentBoxRows.splice(r, 1);
                        }
                    }
                    if (nextRow.length === 0) {
                        // Target row is empty, we need a new box
                        const usedNextSpan = this.calcUsedSpan(nextRow);
                        const newSpan = Math.max((12 - usedNextSpan), 2);
                        const newCCB = new ComponentConfigBox([poppedCC], 0);
                        newCCB.rowIndex = r + 1;
                        newCCB.colIndex = 0;
                        newCCB.colSpan = newSpan;
                        nextRow.push(newCCB);
                    } else {
                        // Tack it onto the first one
                        nextRow[0].componentConfigs.unshift(poppedCC);
                    }
                } else {
                    moveItemInArray(ccBox.componentConfigs, stackIndex, stackIndex + 1);
                }
            } else if (direction === 'LEFT') {
                if (c === 0 && ccBox.componentConfigs.length === 1) {
                    // Already solo on edge
                    return;
                }
                // Either pop to prev or pop to new
                const poppedCC = ccBox.componentConfigs.splice(stackIndex, 1)[0];
                if (ccBox.componentConfigs.length > 0) {
                    // Last box so pop to new
                    const usedCurrentSpan = this.calcUsedSpan(row);
                    const newSpan = Math.max((12 - usedCurrentSpan), 2);
                    const newCCB = new ComponentConfigBox([poppedCC], 0);
                    newCCB.rowIndex = r;
                    newCCB.colIndex = 0;
                    newCCB.colSpan = newSpan;
                    row.splice(c, 0, newCCB);
                } else {
                    // Pop to prev as nothing is left from where we came
                    const leftCCBox = row[c - 1];
                    leftCCBox.componentConfigs.push(poppedCC);
                    // Nothing left so remove
                    row.splice(c, 1);
                }
            } else if (direction === 'RIGHT') {
                if (c === (row.length - 1) && ccBox.componentConfigs.length === 1) {
                    // Already solo on edge
                    return;
                }
                // Either pop to prev or pop to new
                const poppedCC = ccBox.componentConfigs.splice(stackIndex, 1)[0];
                if (ccBox.componentConfigs.length > 0) {
                    // Last box so pop to new
                    const usedCurrentSpan = this.calcUsedSpan(row);
                    const newSpan = Math.max((12 - usedCurrentSpan), 2);
                    const newCCB = new ComponentConfigBox([poppedCC], 0);
                    newCCB.rowIndex = r;
                    newCCB.colIndex = 0;
                    newCCB.colSpan = newSpan;
                    row.splice(c + 1, 0, newCCB);
                } else {
                    // Pop to next as nothing is left from where we came
                    const rightCCBox = row[c + 1];
                    rightCCBox.componentConfigs.unshift(poppedCC);
                    // Nothing left so remove
                    row.splice(c, 1);
                }
            }
            this.reindexAllComponentBoxes();
            // Set the selection to whatever component we moved
            this.componentBoxRows
                .forEach((componentBoxRow, rowIndex) => {
                    componentBoxRow
                        .forEach((ccb, columnIndex) => {
                            ccb.componentConfigs
                                .forEach((cc, stackIndex) => {
                                    if (cc.identifier === selectedIdentifier) {
                                        this.selectedRow = rowIndex;
                                        this.selectedCol = columnIndex;
                                        this.selectedStackIndex = stackIndex;
                                    }
                                });
                        });
                });
        }
    }

    finalizeToComponentLayouts() {
        // Set the internal layout values in actual component
        const identifiers = [];
        this.componentBoxRows
            .forEach((componentsRow) => {
                componentsRow
                    .forEach((ccBox) => {
                        ccBox.updateComponentConfig();
                        ccBox.componentConfigs
                            .forEach((cc) => {
                                identifiers.push(cc.identifier);
                            });
                    });
            });
        // Reorder the components to match the order inused in the layout
        this.reportDashboardConfig.componentConfigs = this.reportDashboardConfig.componentConfigs
            .sort((cc1, cc2) => {
                return identifiers.indexOf(cc1.identifier) - identifiers.indexOf(cc2.identifier);
            });
    }

    componentSummary(cc: ReportDashboardComponentConfig): string {
        if (cc.type === 'TABLE' || cc.type === 'COMPARISON_TABLE') {
            return (cc.table?.colAttributes || []).join(',');
        } else if (cc.type === 'PIVOT') {
            return `${(cc.pivot?.colAttributes || []).join(',')} by ${(cc.pivot?.rowAttributes || []).join(',')} showing ${(cc.pivot?.sumAttributes || []).join(',')}`;
        } else if (cc.type === 'CHART') {
            return `${cc.chart?.xAttribute}  vs ${cc.chart?.yAttribute}`;
        }
    }

    setSelected(r, c, stackIndex) {
        this.selectedRow = r;
        this.selectedCol = c;
        this.selectedStackIndex = stackIndex;
    }

    isSelected(r, c, stackIndex): boolean {
        return r === this.selectedRow && c === this.selectedCol && stackIndex === this.selectedStackIndex;
    }
}
